import { useId } from "react";
import { cn } from "@/lib/utils";

import type { TypeSummary } from "./types";

// Phase 4i catalog-refinement — shared sort affordance for the
// CategoryLandingPage + SearchResultsPage. Client-side only: data is
// always <100 types per category/search, so re-sorting in memory
// keeps the UX snappy and avoids a new server param.
//
// `default` preserves the server's order (categories.position then
// equipment_types.position) — the order the operator curated in
// admin. The other three are direct user intent.

export type CatalogSortValue =
  | "default"
  | "name_asc"
  | "price_asc"
  | "price_desc";

export interface CatalogSortOption {
  value: CatalogSortValue;
  label: string;
}

export const CATALOG_SORT_OPTIONS: readonly CatalogSortOption[] = [
  { value: "default",    label: "Featured" },
  { value: "name_asc",   label: "Name (A–Z)" },
  { value: "price_asc",  label: "Price: low to high" },
  { value: "price_desc", label: "Price: high to low" }
] as const;

export const DEFAULT_SORT: CatalogSortValue = "default";

export function isCatalogSortValue(value: unknown): value is CatalogSortValue {
  return (
    typeof value === "string" &&
    CATALOG_SORT_OPTIONS.some((opt) => opt.value === value)
  );
}

interface CatalogSortSelectProps {
  value: CatalogSortValue;
  onChange: (next: CatalogSortValue) => void;
  className?: string;
}

export function CatalogSortSelect({
  value,
  onChange,
  className
}: CatalogSortSelectProps): JSX.Element {
  const id = useId();
  return (
    <div className={cn("flex items-center gap-2", className)}>
      <label
        htmlFor={id}
        className="text-xs font-semibold uppercase tracking-wider text-zinc-600"
      >
        Sort by
      </label>
      <select
        id={id}
        value={value}
        onChange={(event) => onChange(event.target.value as CatalogSortValue)}
        className={cn(
          "rounded-md border border-zinc-300 bg-brand-white px-2.5 py-1.5 text-sm text-brand-black",
          "focus-visible:border-brand-red focus-visible:outline-none focus-visible:ring-2",
          "focus-visible:ring-brand-red/30"
        )}
      >
        {CATALOG_SORT_OPTIONS.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>
    </div>
  );
}

// Pure in-memory sort — never mutates the input array. Quote-only
// types (no weekly rate) sink to the bottom for both price sorts so
// they don't displace browsable inventory.
export function sortTypes(
  types: readonly TypeSummary[],
  sort: CatalogSortValue
): TypeSummary[] {
  const copy = [...types];
  switch (sort) {
    case "default":
      return copy;
    case "name_asc":
      return copy.sort((a, b) => a.name.localeCompare(b.name));
    case "price_asc":
      return copy.sort(byWeeklyPrice("asc"));
    case "price_desc":
      return copy.sort(byWeeklyPrice("desc"));
  }
}

function byWeeklyPrice(
  direction: "asc" | "desc"
): (a: TypeSummary, b: TypeSummary) => number {
  const sign = direction === "asc" ? 1 : -1;
  return (a, b) => {
    const pa = a.from_price_per_week_cents;
    const pb = b.from_price_per_week_cents;
    // Nulls always sink (quote-only inventory at the bottom).
    if (pa == null && pb == null) return 0;
    if (pa == null) return 1;
    if (pb == null) return -1;
    return (pa - pb) * sign;
  };
}
